import { Heart, ShoppingBag, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { imgUrl, formatINR } from "../lib/api";
import { useStore } from "../context/StoreContext";

export function ProductCard({ product, onQuickView, index = 0 }) {
  const { addToCart, toggleWishlist, wishlist } = useStore();
  const wished = wishlist.some((p) => p.id === product.id);
  return (
    <motion.article
      data-testid={`product-card-${product.id}`}
      className="group relative"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="relative overflow-hidden rounded-[1.25rem] bg-white shadow-[0_10px_40px_-18px_rgba(61,43,51,0.35)] transition-transform duration-500 group-hover:-translate-y-1.5">
        <Link to={`/product/${product.id}`} data-testid={`product-link-${product.id}`} aria-label={product.name}>
          <div className="aspect-[3/4] overflow-hidden bg-secondary/40">
            {product.images?.[0] ? (
              <img src={imgUrl(product.images[0])} alt={product.name} loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
            ) : (
              <div className="flex h-full w-full items-center justify-center font-display text-4xl text-primary/40">A</div>
            )}
          </div>
        </Link>
        <div className="absolute left-3 top-3 flex flex-col gap-1.5">
          {product.is_new && <span className="rounded-full bg-accent px-2.5 py-0.5 text-[10px] font-medium tracking-widest text-accent-foreground uppercase">New</span>}
          {product.is_bestseller && <span className="rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-medium tracking-widest text-primary-foreground uppercase">Bestseller</span>}
        </div>
        <div className="absolute right-3 top-3 flex flex-col gap-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <button data-testid={`wishlist-btn-${product.id}`} onClick={() => toggleWishlist(product)} aria-label="Wishlist"
            className={`rounded-full p-2 shadow-md backdrop-blur transition-colors ${wished ? "bg-primary text-white" : "bg-white/85 text-foreground hover:bg-secondary"}`}>
            <Heart size={16} fill={wished ? "currentColor" : "none"} />
          </button>
          {onQuickView && (
            <button data-testid={`quickview-btn-${product.id}`} onClick={() => onQuickView(product)} aria-label="Quick view"
              className="rounded-full bg-white/85 p-2 shadow-md backdrop-blur transition-colors hover:bg-secondary">
              <Eye size={16} />
            </button>
          )}
        </div>
        {product.variants?.length > 0 || product.variant_groups?.length > 0 ? (
          <Link to={`/product/${product.id}`} data-testid={`quickadd-btn-${product.id}`}
            className="absolute inset-x-3 bottom-3 flex translate-y-3 items-center justify-center gap-2 rounded-full bg-foreground/85 py-2.5 text-xs font-medium tracking-widest text-white uppercase opacity-0 backdrop-blur transition-all duration-300 hover:bg-primary group-hover:translate-y-0 group-hover:opacity-100">
            <ShoppingBag size={14} /> Choose design
          </Link>
        ) : (
          <button data-testid={`quickadd-btn-${product.id}`} onClick={() => addToCart(product, 1)}
            className="absolute inset-x-3 bottom-3 flex translate-y-3 items-center justify-center gap-2 rounded-full bg-foreground/85 py-2.5 text-xs font-medium tracking-widest text-white uppercase opacity-0 backdrop-blur transition-all duration-300 hover:bg-primary group-hover:translate-y-0 group-hover:opacity-100">
            <ShoppingBag size={14} /> Add to bag
          </button>
        )}
      </div>
      <div className="px-1 pt-3">
        <Link to={`/product/${product.id}`}>
          <h3 className="font-display text-sm leading-snug sm:text-base">{product.name}</h3>
        </Link>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="text-sm font-medium text-primary" data-testid={`product-price-${product.id}`}>{product.price > 0 ? formatINR(product.price) : "Price on request"}</span>
          {product.price > 0 && product.compare_at_price > product.price && (
            <span className="text-xs text-muted-foreground line-through">{formatINR(product.compare_at_price)}</span>
          )}
        </div>
      </div>
    </motion.article>
  );
}
